// STUB(provisioning-accounts) FILE: the whole file is stubbed data, removed by `node .claude/scripts/remove-stubs.mjs provisioning-accounts`
// Six fake provisioning placeholders (paid Instant accounts not created yet), one per state, added to the
// `provisioning` group of `GET /mine` and `GET /mine/active` until the backend sends it. The id says the
// state; the card shows its short first segment (e.g. #kyc_new), kept short so the badge stays beside it:
// - kyc_new-kyc-not-started-stub-provisioning: KYC NOT_STARTED, the card links to Profile > Verification
// - kyc_review-kyc-in-review-stub-provisioning: KYC IN_PROGRESS (under review), same link
// - kyc_failed-kyc-rejected-stub-provisioning: KYC FAILED (rejected), same link
// - sign_wait-contract-not-sent-stub-provisioning: KYC COMPLETED, contract NOT_STARTED, the card
//   links to Contracts
// - sign_ready-contract-ready-to-sign-stub-provisioning: KYC COMPLETED, contract IN_PROGRESS (ready
//   to sign), same link; it also gets a fake first row on the Contracts page
// - activating-both-checks-done-stub-provisioning: both COMPLETED, the account is being created (not
//   clickable)
// Opened by URL, `GET /challenges/:id` answers 404 for these ids (like the backend does for a challenge
// that does not exist yet), so the detail page redirects to the state's next step.
import { HttpErrorResponse } from "@angular/common/http";
import {
  type ChallengeProvisioningDetails,
  type ChallengeTradesPaginated,
  type GroupedChallenges,
  KycStatus,
  type PaginationPayload,
  type PlatformType,
  type ProvisioningCheckStatus,
} from "@models";
import { map, Observable, of, throwError } from "rxjs";
import type { LegacyResponse } from "../../core/service/response.model";
import { type Contract, ContractStatus } from "../model/contracts.model";

const CONTRACT_ID: string = "sign_ready-contract-ready-to-sign-stub-provisioning";

function toDetails(
  challengeId: string,
  type: PlatformType,
  kyc: ProvisioningCheckStatus,
  contract: ProvisioningCheckStatus,
): ChallengeProvisioningDetails {
  return {
    challengeId,
    challengeDefinitionId: "USD100000-SWAPS-INSTANT",
    planetName: "Saturn",
    accountSize: 100000,
    accountCurrency: "USD",
    chargeSwapFees: true,
    evaluationType: "INSTANT",
    addonIds: [],
    createdAt: "2026-11-05T10:00:00",
    checks: [
      { type: "KYC", status: kyc, updatedAt: "2026-11-05T10:05:00" },
      { type: "CONTRACT", status: contract, updatedAt: "2026-11-05T10:05:00" },
    ],
    subscriptionType: "FUNDED",
    type,
  };
}

const PROVISIONING_STUB_DETAILS: ChallengeProvisioningDetails[] = [
  toDetails("kyc_new-kyc-not-started-stub-provisioning", "DXTRADE", "NOT_STARTED", "NOT_STARTED"),
  toDetails("kyc_review-kyc-in-review-stub-provisioning", "MT5", "IN_PROGRESS", "NOT_STARTED"),
  toDetails("kyc_failed-kyc-rejected-stub-provisioning", "CTRADER", "FAILED", "NOT_STARTED"),
  toDetails("sign_wait-contract-not-sent-stub-provisioning", "DXTRADE", "COMPLETED", "NOT_STARTED"),
  toDetails(CONTRACT_ID, "MT5", "COMPLETED", "IN_PROGRESS"),
  toDetails("activating-both-checks-done-stub-provisioning", "CTRADER", "COMPLETED", "COMPLETED"),
];

/** The grouped challenges with the stub placeholders before any real ones. */
export function withProvisioningStub(grouped: GroupedChallenges): GroupedChallenges {
  return {
    ...grouped,
    provisioning: [...PROVISIONING_STUB_DETAILS, ...(grouped.provisioning ?? [])],
  };
}

/** Fake first row on the Contracts page: the contract of the "KYC done" stub placeholder, ready to sign. */
export const PROVISIONING_STUB_CONTRACTS: Contract[] = [
  {
    phase2ChallengeId: CONTRACT_ID,
    phase2Account: "",
    accountSize: "100000",
    planetName: "SATURN",
    kycStatus: KycStatus.PASSED,
    contractStatus: ContractStatus.READY_TO_SIGN,
    evaluationType: "INSTANT",
    orderId: CONTRACT_ID,
  },
];

export function isProvisioningStubId(challengeId: string): boolean {
  return PROVISIONING_STUB_DETAILS.some(
    (details: ChallengeProvisioningDetails) => details.challengeId === challengeId,
  );
}

/** Fake `GET /challenges/:id`: a placeholder is not a challenge yet. */
export function getProvisioningStubChallengeError$(challengeId: string): Observable<never> {
  return throwError(
    () => new HttpErrorResponse({ status: 404, statusText: "Not Found", url: challengeId }),
  );
}

/** Fake `GET /trades/history/:id`, always empty: a placeholder has no trading account yet. */
export function getProvisioningStubTrades$(
  paginationPayload: PaginationPayload,
): Observable<ChallengeTradesPaginated> {
  return of({
    items: [],
    totalItems: 0,
    pageInfo: {
      currentPage: paginationPayload.page,
      size: paginationPayload.size,
      first: true,
      last: true,
      totalPages: 0,
    },
  });
}

/** Always on while this stub exists; lets the service add the stub with a removable early return. */
export const PROVISIONING_STUB_ENABLED: boolean = true;

/** Adds the stub placeholders to `GET /mine` (legacy envelope). */
export function withProvisioningStubLegacy(
  response$: Observable<LegacyResponse<GroupedChallenges>>,
): Observable<LegacyResponse<GroupedChallenges>> {
  return response$.pipe(
    map(
      (response: LegacyResponse<GroupedChallenges>): LegacyResponse<GroupedChallenges> => ({
        ...response,
        data: response.data ? withProvisioningStub(response.data) : response.data,
      }),
    ),
  );
}

/** Adds the stub placeholders to `GET /mine/active`. */
export function withProvisioningStub$(
  response$: Observable<GroupedChallenges>,
): Observable<GroupedChallenges> {
  return response$.pipe(map(withProvisioningStub));
}
